// src/ui/components/claweyMascot.ts — Inline SVG Mascot with 3 Poses (NFR-RENDER-001)

export type ClaweyPose = 'idle' | 'thinking' | 'celebrating' | 'dancing';

export interface ClaweyMascotOptions {
  initialPose?: ClaweyPose;
  size?: number; // Size in px (default 140)
  onClick?: () => void;
}

export class ClaweyMascot {
  private element: HTMLElement;
  private currentPose: ClaweyPose;
  private size: number;
  private onClick?: () => void;

  constructor(options: ClaweyMascotOptions = {}) {
    this.currentPose = options.initialPose || 'idle';
    this.size = options.size || 140;
    this.onClick = options.onClick;

    this.element = document.createElement('div');
    this.element.className = 'clawey-mascot-container';
    this.element.style.width = `${this.size}px`;
    this.element.style.height = `${this.size}px`;
    this.element.style.cursor = 'pointer';
    this.element.style.touchAction = 'manipulation';
    this.element.setAttribute('role', 'button');
    this.element.setAttribute('aria-label', `Clawey the Lobster (${this.currentPose} pose)`);

    this.render();

    this.element.addEventListener('click', () => {
      if (this.onClick) {
        this.onClick();
      }
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  setPose(pose: ClaweyPose): void {
    if (this.currentPose === pose) return;
    this.currentPose = pose;
    this.element.setAttribute('aria-label', `Clawey the Lobster (${this.currentPose} pose)`);
    this.render();
  }

  getPose(): ClaweyPose {
    return this.currentPose;
  }

  private render(): void {
    this.element.innerHTML = `
      <div class="atelier-clawey-medallion" style="
        width: ${this.size}px;
        height: ${this.size}px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <img 
          src="/assets/atelier/clawey-brass-porthole.png" 
          alt="Clawey" 
          style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%; pointer-events: none;" 
        />
        <div style="position: absolute; inset: 0; pointer-events: none; opacity: 0; width: 0; height: 0; overflow: hidden;" aria-hidden="true">
          ${this.getSvgMarkup()}
        </div>
      </div>
    `;
  }

  private getSvgMarkup(): string {
    const isCelebrating = this.currentPose === 'celebrating';
    const isDancing = this.currentPose === 'dancing';
    const isThinking = this.currentPose === 'thinking';

    // Claw rotation angles based on pose
    const leftClawTransform = isDancing
      ? 'rotate(-38 60 70) translate(-6, -14)'
      : isCelebrating
        ? 'rotate(-25 60 70) translate(0, -10)'
        : isThinking
          ? 'rotate(20 60 70) translate(10, 5)'
          : 'rotate(0 60 70)';

    const rightClawTransform = isDancing
      ? 'rotate(38 140 70) translate(6, -14)'
      : isCelebrating
        ? 'rotate(25 140 70) translate(0, -10)'
        : isThinking
          ? 'rotate(-10 140 70)'
          : 'rotate(0 140 70)';

    const monocleTransform = isDancing
      ? 'translate(0, -3) scale(1.08)'
      : isThinking
        ? 'translate(-8, -4) scale(1.1)'
        : 'translate(0, 0)';

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="${this.size}" height="${this.size}" class="clawey-svg pose-${this.currentPose} ${isDancing ? 'dancing-mascot' : ''}">
        <style>
          .clawey-svg { overflow: visible; filter: drop-shadow(2px 4px 4px rgba(26, 54, 93, 0.15)); }
          .antenna { animation: antennaWave 3s ease-in-out infinite alternate; transform-origin: center bottom; }
          .antenna-right { animation: antennaWaveRight 3.5s ease-in-out infinite alternate; transform-origin: center bottom; }
          @keyframes antennaWave {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(5deg); }
          }
          @keyframes antennaWaveRight {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-5deg); }
          }
          .sparkle { animation: sparklePulse 1.2s ease-in-out infinite alternate; }
          @keyframes sparklePulse {
            0% { opacity: 0.3; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1.2); }
          }
          .dancing-mascot { animation: claweyDance 0.8s ease-in-out infinite alternate; transform-origin: center bottom; }
          @keyframes claweyDance {
            0% { transform: rotate(-4deg) translateY(0); }
            100% { transform: rotate(4deg) translateY(-8px); }
          }
        </style>

        ${(isCelebrating || isDancing)
        ? `
          <!-- Celebration Golden & Bioluminescent Sparkles -->
          <circle cx="35" cy="40" r="4.5" fill="#C8963E" class="sparkle" />
          <circle cx="165" cy="40" r="4.5" fill="#C8963E" class="sparkle" />
          <circle cx="100" cy="20" r="5" fill="#C8963E" class="sparkle" />
          ${isDancing ? `
            <circle cx="20" cy="80" r="3.5" fill="#00E5FF" class="sparkle" />
            <circle cx="180" cy="80" r="3.5" fill="#00E5FF" class="sparkle" />
            <circle cx="60" cy="18" r="3" fill="#FFE58F" class="sparkle" />
            <circle cx="140" cy="18" r="3" fill="#FFE58F" class="sparkle" />
          ` : ''}
        `
        : ''
      }

        <!-- Antennae (Victorian pen strokes) -->
        <path d="M 94 75 Q 65 30, 35 25" fill="none" stroke="#C8963E" stroke-width="2.5" stroke-linecap="round" class="antenna" />
        <path d="M 106 75 Q 135 30, 165 25" fill="none" stroke="#C8963E" stroke-width="2.5" stroke-linecap="round" class="antenna-right" />

        <!-- Left Pincer Claw -->
        <g transform="${leftClawTransform}" style="transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);">
          <path d="M 52 80 C 35 65, 38 40, 60 48 C 68 51, 74 65, 68 84 C 62 84, 56 83, 52 80 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2.5"/>
          <path d="M 42 56 C 32 62, 38 76, 52 80" fill="none" stroke="#1A365D" stroke-width="2.2"/>
        </g>

        <!-- Right Crusher Claw -->
        <g transform="${rightClawTransform}" style="transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);">
          <path d="M 148 80 C 165 65, 162 40, 140 48 C 132 51, 126 65, 132 84 C 138 84, 144 83, 148 80 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2.5"/>
          <path d="M 158 56 C 168 62, 162 76, 148 80" fill="none" stroke="#1A365D" stroke-width="2.2"/>
        </g>

        <!-- Lobster Carapace / Body -->
        <ellipse cx="100" cy="108" rx="28" ry="38" fill="#9E3D2B" stroke="#1A365D" stroke-width="2.5"/>

        <!-- Segmented Tail -->
        <path d="M 82 142 C 82 152, 118 152, 118 142 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2"/>
        <path d="M 85 152 C 85 161, 115 161, 115 152 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2"/>
        <path d="M 88 161 C 88 170, 112 170, 112 161 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2"/>
        <!-- Tail Fan -->
        <path d="M 100 170 L 80 184 C 100 188, 120 188, 120 184 Z" fill="#9E3D2B" stroke="#1A365D" stroke-width="2"/>

        <!-- Eyes & Victorian Monocle -->
        <!-- Left Eye -->
        <circle cx="90" cy="88" r="4.5" fill="#1A365D"/>
        <circle cx="88.5" cy="86.5" r="1.5" fill="#FAF6ED"/>

        <!-- Right Eye with Brass Monocle -->
        <g transform="${monocleTransform}" style="transition: transform 0.3s ease;">
          <circle cx="110" cy="88" r="7.5" fill="#FAF6ED" stroke="#C8963E" stroke-width="2.2"/>
          <circle cx="110" cy="88" r="4.5" fill="#1A365D"/>
          <circle cx="108.5" cy="86.5" r="1.5" fill="#FAF6ED"/>
          <path d="M 117 90 Q 124 98, 120 110" fill="none" stroke="#C8963E" stroke-width="1.5"/>
        </g>
      </svg>
    `;
  }
}
